import { baseApi } from 'shared/api/base-api';
import type { MatchResolverContext, ResolverMatchSlot, ResolverTeam } from 'modules/manager/domain/placeholder';

interface RenderTeamDto {
  id: number;
  name: string;
  shortName: string;
  emblem?: { externalUrl?: string };
}

interface RenderTeamMatchDto {
  teamOrder?: number;
  score?: number;
  team?: RenderTeamDto;
}

interface RenderMatchDto {
  id: number;
  date?: string;
  location?: { name?: string };
  tournament?: { id: number; name: string; images?: { externalUrl?: string }[] };
  championship?: { id: number; name: string; images?: { externalUrl?: string }[] };
  round?: { name?: string } | null;
  teamMatches?: RenderTeamMatchDto[];
}

interface RenderDataResponse {
  match: RenderMatchDto | null;
}

export interface RenderDataArgs {
  /**
   * Ordered list of match ids the canvas should render. For single-match
   * templates (SCHEDULE / RESULTS) pass `[matchId]`. For multi-match templates
   * (MULTIPLE_SCHEDULE / MULTIPLE_RESULTS) pass one id per slot, in the order
   * placeholders' `matchNumber` indexes into.
   */
  matchIds: number[];
}

const RENDER_DATA_QUERY = `#graphql
query getRenderData($matchId: Int!) {
  match(id: $matchId) {
    id
    date
    location { name }
    tournament { id name images { externalUrl } }
    championship { id name images { externalUrl } }
    round { name }
    teamMatches {
      teamOrder
      score
      team {
        id
        name
        shortName
        emblem { externalUrl }
      }
    }
  }
}
`;

export const renderApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getRenderContext: build.query<MatchResolverContext, RenderDataArgs>({
      // Why a queryFn instead of `query`: the backend has no batch-match
      // endpoint, so multi-match templates need N parallel single-match
      // requests stitched into one ResolverContext. Keeping it inside RTK
      // Query (rather than dispatching N separate hooks from the page) gives
      // us a single loading/error state for the orchestrator to react to.
      async queryFn({ matchIds }, _api, _extra, baseQuery) {
        if (matchIds.length === 0) return { error: { status: 'CUSTOM_ERROR', error: 'No matchIds provided' } as never };
        const responses = await Promise.all(
          matchIds.map((matchId) =>
            baseQuery({ query: RENDER_DATA_QUERY, variables: { matchId } }),
          ),
        );
        const matches: RenderMatchDto[] = [];
        for (const res of responses) {
          if (res.error) return { error: res.error };
          const match = (res.data as RenderDataResponse | undefined)?.match;
          if (!match) return { error: { status: 'CUSTOM_ERROR', error: 'Match not found' } as never };
          matches.push(match);
        }

        const slots: ResolverMatchSlot[] = [];
        for (const match of matches) {
          const home = pickTeam(match.teamMatches, 1);
          const away = pickTeam(match.teamMatches, 2);
          if (!home || !away) return { error: { status: 'CUSTOM_ERROR', error: 'Match is missing one of the teams' } as never };
          slots.push({
            match: {
              id: match.id,
              date: match.date,
              venue: match.location?.name,
              homeScore: home.score,
              awayScore: away.score,
              roundName: match.round?.name,
            },
            homeTeam: toResolverTeam(home),
            awayTeam: toResolverTeam(away),
          });
        }
        // Why the first match wins for tournament/championship: those fields
        // are shared across the canvas (the whole template is "Premier Cup
        // round 5") — the data model assumes every slot belongs to the same
        // parent. If callers mix tournaments the visible label simply tracks
        // slot #1, which is consistent with how the single-match render
        // already worked.
        const firstMatch = matches[0];
        const head = slots[0];
        return {
          data: {
            tournament: {
              id: firstMatch.tournament?.id ?? 0,
              name: firstMatch.tournament?.name ?? '',
              logoUrl: firstMatch.tournament?.images?.[0]?.externalUrl,
            },
            championship: {
              id: firstMatch.championship?.id ?? 0,
              name: firstMatch.championship?.name ?? '',
              logoUrl: firstMatch.championship?.images?.[0]?.externalUrl,
            },
            match: head.match,
            homeTeam: head.homeTeam,
            awayTeam: head.awayTeam,
            matches: slots,
          },
        };
      },
      keepUnusedDataFor: 30,
    }),
  }),
  overrideExisting: false,
});

function pickTeam(teamMatches: RenderTeamMatchDto[] | undefined, order: number): RenderTeamMatchDto | undefined {
  return teamMatches?.find((tm) => tm.teamOrder === order);
}

function toResolverTeam(tm: RenderTeamMatchDto): ResolverTeam {
  const team = tm.team;
  return {
    id: team?.id ?? 0,
    name: team?.name ?? '',
    shortName: team?.shortName ?? '',
    logoUrl: team?.emblem?.externalUrl,
  };
}

export const { useGetRenderContextQuery } = renderApi;
